/**
 * `/admin/products` — operator CRUD for the customer-facing catalog
 * (OCP-157). Pairs with `/v1/admin/products` backend routes. Operators
 * use this to:
 *   - sync a product to Polar (push-to-create or paste an existing id)
 *   - toggle products active/inactive
 *   - add new tiers / addon products
 *
 * Polar-only for now (OCP-341): the Paddle column / field / sync badge
 * are hidden. The backend + data model keep paddle_product_id and the
 * Paddle sync state, so Paddle can be re-surfaced later without a
 * migration. See OCP-178 (multi-provider epic).
 *
 * Includes the `include_inactive` filter so operators can see + revive
 * archived rows. Customer-facing catalog only reads active=true.
 */
import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';

import { api, ApiError } from '../lib/api';

type SyncState = 'synced' | 'drift' | 'not_pushed' | 'orphan_remote';

interface SyncSnapshot {
  display_name: string;
  price_eur_cents: number;
  billing_period: string;
  /** OCP-1036 - a seat price's tiers; locally always one tier at the list price. */
  seat_tiers?: Array<{ min_seats: number; price_per_seat_cents: number }>;
}

interface SyncStateInfo {
  state: SyncState;
  drift_detected_at: string | null;
  remote_snapshot: SyncSnapshot | null;
  local_snapshot: SyncSnapshot | null;
}

interface Product {
  id: string;
  display_name: string;
  product_family: 'orboto-saas' | 'orboto-self-host' | 'oms' | 'backup' | 'other';
  price_eur_cents: number;
  billing_period: 'one-time' | 'monthly' | 'yearly' | 'metered';
  paddle_product_id: string | null;
  polar_product_id: string | null;
  features: Record<string, unknown>;
  overage_config: Record<string, unknown> | null;
  description: string | null;
  marketing_bullets: string[] | null;
  active: boolean;
  created_at: string;
  updated_at: string;
  sync_state?: { paddle?: SyncStateInfo; polar?: SyncStateInfo };
}

const FAMILIES = ['orboto-saas', 'orboto-self-host', 'oms', 'backup', 'other'] as const;
const PERIODS = ['one-time', 'monthly', 'yearly', 'metered'] as const;

export function AdminProductsPage() {
  const [items, setItems] = useState<Product[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [includeInactive, setIncludeInactive] = useState(true);
  const [familyFilter, setFamilyFilter] = useState<string>('');
  const [editing, setEditing] = useState<Product | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    const params = new URLSearchParams();
    if (includeInactive) params.set('include_inactive', 'true');
    if (familyFilter) params.set('family', familyFilter);
    try {
      const r = await api<{ items: Product[] }>(
        `/v1/admin/products${params.toString() ? `?${params}` : ''}`,
      );
      setItems(r.items);
    } catch (err) {
      setError(err instanceof ApiError ? `HTTP ${err.status}` : 'Load failed');
    }
  }, [includeInactive, familyFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div>
      <header className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Products</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Customer-facing product catalog. Customers see active rows on
            account.orboto.io/products and on per-product subscribe pages.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
        >
          New product
        </button>
      </header>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-gray-600 dark:text-gray-400">Product family</span>
          <select
            value={familyFilter}
            onChange={(e) => setFamilyFilter(e.target.value)}
            className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
          >
            <option value="">All</option>
            {FAMILIES.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
            className="h-4 w-4 accent-brand-500"
          />
          Include inactive
        </label>
      </div>

      {error && (
        <div className="mb-4 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {!items && <p className="text-sm text-gray-500">Loading…</p>}

      {items && items.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-gray-500">
          No products match. Click "New product" to add one.
        </div>
      )}

      {items && items.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">ID</th>
                <th className="px-3 py-2">Display name</th>
                <th className="px-3 py-2">Family</th>
                <th className="px-3 py-2 text-right">Price (€)</th>
                <th className="px-3 py-2">Period</th>
                <th className="px-3 py-2">Sync</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {items.map((p) => (
                <tr key={p.id} className={!p.active ? 'opacity-60' : ''}>
                  <td className="px-3 py-2 font-mono text-xs">{p.id}</td>
                  <td className="px-3 py-2 font-medium">{p.display_name}</td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{p.product_family}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {(p.price_eur_cents / 100).toFixed(2)}
                  </td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{p.billing_period}</td>
                  <td className="px-3 py-2">
                    <SyncBadge
                      provider="polar"
                      info={p.sync_state?.polar}
                      onClick={() => setEditing(p)}
                    />
                  </td>
                  <td className="px-3 py-2">
                    {p.active ? (
                      <span className="rounded border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 px-2 py-0.5 text-xs text-emerald-800 dark:text-emerald-300">
                        active
                      </span>
                    ) : (
                      <span className="rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 px-2 py-0.5 text-xs text-gray-600 dark:text-gray-400">
                        inactive
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => setEditing(p)}
                      className="text-xs text-brand-500 hover:underline"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <ProductModal
          mode="create"
          initial={null}
          onCancel={() => setCreating(false)}
          onSaved={async () => {
            setCreating(false);
            await load();
          }}
        />
      )}
      {editing && (
        <ProductModal
          mode="edit"
          initial={editing}
          onCancel={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

function ProductModal({
  mode,
  initial,
  onCancel,
  onSaved,
}: {
  mode: 'create' | 'edit';
  initial: Product | null;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [id, setId] = useState(initial?.id ?? '');
  const [displayName, setDisplayName] = useState(initial?.display_name ?? '');
  const [family, setFamily] = useState<Product['product_family']>(
    initial?.product_family ?? 'oms',
  );
  const [priceEur, setPriceEur] = useState(
    initial ? (initial.price_eur_cents / 100).toFixed(2) : '0',
  );
  const [period, setPeriod] = useState<Product['billing_period']>(
    initial?.billing_period ?? 'monthly',
  );
  const [polarId, setPolarId] = useState(initial?.polar_product_id ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  // Bullets edited as newline-separated lines for ergonomic editing.
  // Round-trip to array on submit, joined back on init.
  const [bulletsText, setBulletsText] = useState(
    (initial?.marketing_bullets ?? []).join('\n'),
  );
  const [active, setActive] = useState(initial?.active ?? true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pushing, setPushing] = useState<'polar' | null>(null);
  const [pushError, setPushError] = useState<string | null>(null);

  async function pushToProvider(provider: 'polar') {
    if (!initial) return;
    setPushing(provider);
    setPushError(null);
    try {
      const updated = await api<Product>(
        `/v1/admin/products/${initial.id}/sync/${provider}`,
        { method: 'POST' },
      );
      setPolarId(updated.polar_product_id ?? '');
    } catch (err) {
      if (err instanceof ApiError) {
        // OCP-606 - the provider's validation text rides in `detail`.
        const body = err.body as { error?: string; detail?: string } | null;
        const msg = body?.error ?? `HTTP ${err.status}`;
        setPushError(body?.detail ? `${provider}: ${msg} - ${body.detail}` : `${provider}: ${msg}`);
      } else {
        setPushError(`${provider}: push failed`);
      }
    } finally {
      setPushing(null);
    }
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    const bullets = bulletsText
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    const body = {
      display_name: displayName,
      product_family: family,
      price_eur_cents: Math.round(Number(priceEur) * 100),
      billing_period: period,
      // Paddle hidden from the UI (Polar-only); preserve any existing id.
      paddle_product_id: initial?.paddle_product_id ?? null,
      polar_product_id: polarId.trim() || null,
      description: description.trim() ? description.trim() : null,
      marketing_bullets: bullets.length > 0 ? bullets : null,
      active,
    };
    try {
      if (mode === 'create') {
        await api('/v1/admin/products', { method: 'POST', body: { id, ...body } });
      } else {
        await api(`/v1/admin/products/${initial!.id}`, { method: 'PATCH', body });
      }
      await onSaved();
    } catch (err) {
      if (err instanceof ApiError) {
        const errorBody = err.body as { error?: string } | null;
        if (errorBody?.error === 'product_id_taken') {
          setError(`Product ID "${id}" is already in use.`);
        } else {
          setError(`Save failed (HTTP ${err.status}).`);
        }
      } else {
        setError('Save failed.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4"
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-xl"
      >
        <h2 className="mb-4 text-base font-semibold">
          {mode === 'create' ? 'New product' : `Edit ${initial!.display_name}`}
        </h2>
        {mode === 'edit' && initial?.sync_state && (
          <div className="mb-3 space-y-2">
            {(['polar'] as const).map((p) => {
              const info = initial.sync_state?.[p];
              if (!info || info.state !== 'drift' || !info.local_snapshot || !info.remote_snapshot) return null;
              return (
                <DriftBanner
                  key={p}
                  provider={p}
                  productId={initial.id}
                  info={info}
                  onResolved={onSaved}
                />
              );
            })}
          </div>
        )}
        <form onSubmit={submit} className="flex flex-col gap-3 text-sm">
          {mode === 'create' && (
            <label className="flex flex-col gap-1">
              <span className="text-xs text-gray-600 dark:text-gray-400">
                ID (lowercase, hyphens — e.g. <code>mail-developer</code>)
              </span>
              <input
                type="text"
                value={id}
                onChange={(e) => setId(e.target.value)}
                required
                className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 font-mono text-xs"
              />
            </label>
          )}
          <label className="flex flex-col gap-1">
            <span className="text-xs text-gray-600 dark:text-gray-400">Display name</span>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5"
            />
          </label>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-gray-600 dark:text-gray-400">Family</span>
              <select
                value={family}
                onChange={(e) => setFamily(e.target.value as Product['product_family'])}
                className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5"
              >
                {FAMILIES.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-gray-600 dark:text-gray-400">Price (€)</span>
              <input
                type="number"
                step="0.01"
                min={0}
                value={priceEur}
                onChange={(e) => setPriceEur(e.target.value)}
                required
                className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 tabular-nums"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-gray-600 dark:text-gray-400">Period</span>
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value as Product['billing_period'])}
                className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5"
              >
                {PERIODS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-gray-600 dark:text-gray-400">
              Polar product ID (Push to create on Polar, or paste an existing
              ID from the Polar dashboard)
            </span>
            <div className="flex gap-2">
              <input
                type="text"
                value={polarId}
                onChange={(e) => setPolarId(e.target.value)}
                placeholder="00000000-0000-0000-0000-000000000000"
                className="flex-1 rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 font-mono text-xs"
              />
              {mode === 'edit' && (
                <button
                  type="button"
                  disabled={polarId.trim().length > 0 || pushing !== null}
                  onClick={() => pushToProvider('polar')}
                  className="rounded bg-brand-500 px-3 py-1 text-xs font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {pushing === 'polar' ? 'Pushing…' : 'Push to Polar'}
                </button>
              )}
            </div>
          </label>
          {pushError && (
            <div className="rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-2 py-1 text-xs text-red-700 dark:text-red-300">
              Push failed — {pushError}
            </div>
          )}

          {/* Customer-facing marketing copy. Markdown supported. */}
          <label className="flex flex-col gap-1">
            <span className="text-xs text-gray-600 dark:text-gray-400">
              Description (shown on customer product card — markdown ok)
            </span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="e.g. For developers building transactional email into their own apps."
              className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-gray-600 dark:text-gray-400">
              Bullets (one per line — each line is markdown). Leave empty to
              fall back to auto-derived feature bullet.
            </span>
            <textarea
              value={bulletsText}
              onChange={(e) => setBulletsText(e.target.value)}
              rows={5}
              placeholder={'**100 emails / month**\nGenerous for testing + hobby projects\nAll features, no surprise upgrades'}
              className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
            />
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              className="h-4 w-4 accent-brand-500"
            />
            <span>Active (visible to customers)</span>
          </label>

          {error && (
            <div className="rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-xs text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/60"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:bg-gray-400"
            >
              {submitting ? '…' : mode === 'create' ? 'Create' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DriftBanner({
  provider,
  productId,
  info,
  onResolved,
}: {
  provider: 'paddle' | 'polar';
  productId: string;
  info: SyncStateInfo;
  onResolved: () => Promise<void>;
}) {
  const [resolving, setResolving] = useState<'push' | 'dismiss' | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function resolve(action: 'push' | 'dismiss') {
    setResolving(action);
    setError(null);
    try {
      if (action === 'push') {
        // OCP-1036 - the explicit push answers the provider's result (the
        // catalog price included), so a failure shows here instead of being
        // swallowed like the best-effort sync on PATCH.
        await api(`/v1/admin/products/${productId}/push/${provider}`, { method: 'POST' });
      } else {
        await api(
          `/v1/admin/products/${productId}/sync-drift/${provider}/dismiss`,
          { method: 'POST' },
        );
      }
      await onResolved();
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; detail?: string } | null;
        setError(`HTTP ${err.status}${body?.error ? ` ${body.error}` : ''}${body?.detail ? `: ${body.detail}` : ''}`);
      } else {
        setError('Failed.');
      }
    } finally {
      setResolving(null);
    }
  }

  const local = info.local_snapshot!;
  const remote = info.remote_snapshot!;
  type SnapshotField = keyof typeof local;
  const fields: SnapshotField[] = ['display_name', 'price_eur_cents', 'billing_period', 'seat_tiers'];
  const show = (v: unknown) =>
    Array.isArray(v)
      ? v.map((t: { min_seats: number; price_per_seat_cents: number }) => `${t.min_seats}+: ${t.price_per_seat_cents}`).join(', ')
      : v === undefined
        ? '-'
        : String(v);

  return (
    <div className="rounded border border-amber-300 bg-amber-50 dark:bg-amber-900/30 p-3 text-xs">
      <div className="mb-2 font-semibold text-amber-800 dark:text-amber-200">
        ⚠ {provider} drift detected
      </div>
      <div className="overflow-x-auto">
      <table className="w-full min-w-[480px] text-xs">
        <thead className="text-gray-500">
          <tr>
            <th className="text-left">Field</th>
            <th className="text-left">Local (OCP)</th>
            <th className="text-left">Remote ({provider})</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((f) => {
            const localVal = show(local[f]);
            const remoteVal = show(remote[f]);
            if (f === 'seat_tiers' && local[f] === undefined && remote[f] === undefined) return null;
            const diff = localVal !== remoteVal;
            return (
              <tr key={f} className={diff ? 'bg-amber-100/50' : ''}>
                <td className="py-0.5">{f}</td>
                <td className="py-0.5 font-mono">{localVal}</td>
                <td className="py-0.5 font-mono">{remoteVal}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          disabled={resolving !== null}
          onClick={() => resolve('push')}
          className="rounded bg-amber-600 px-2 py-1 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
        >
          {resolving === 'push' ? 'Pushing…' : 'Push local → remote'}
        </button>
        <button
          type="button"
          disabled={resolving !== null}
          onClick={() => resolve('dismiss')}
          className="rounded border border-amber-300 bg-white dark:bg-gray-900 px-2 py-1 text-xs hover:bg-amber-100 disabled:opacity-50"
        >
          {resolving === 'dismiss' ? 'Dismissing…' : 'Dismiss (accept drift)'}
        </button>
        {error && <span className="text-xs text-red-700 dark:text-red-300">{error}</span>}
      </div>
    </div>
  );
}

function SyncBadge({
  provider,
  info,
  onClick,
}: {
  provider: 'paddle' | 'polar';
  info: SyncStateInfo | undefined;
  onClick: () => void;
}) {
  if (!info || info.state === 'not_pushed') {
    return (
      <span className="text-[10px] uppercase text-gray-400">
        {provider}: —
      </span>
    );
  }
  if (info.state === 'synced') {
    return (
      <span className="text-[10px] uppercase text-emerald-700 dark:text-emerald-300">
        {provider}: ✓ synced
      </span>
    );
  }
  if (info.state === 'drift') {
    return (
      <button
        type="button"
        onClick={onClick}
        className="text-left text-[10px] uppercase text-amber-700 dark:text-amber-300 hover:underline"
        title="Local + remote diverged. Click to open the diff modal."
      >
        {provider}: ⚠ drift
      </button>
    );
  }
  return (
    <span className="text-[10px] uppercase text-gray-500">
      {provider}: {info.state}
    </span>
  );
}
